import { Configuration } from '@icc/shared/types';
import { PriceSegment, DiscountGroups, PricePart } from '@icc/price/b2b';
import { TimeLimitSegment } from '@icc/configurator/types';
import { RollerShutter } from '../elements/roller-shutter';
import { RollerShutterParameters } from '../elements/roller-shutter-parameters';
import { RollerShutterActiveConfiguration } from './roller-shutter-active-configuration';
import { Accessory } from '@icc/construction-elements/types';

import { core } from '@icc/shared/helpers';

import { changesInVersion4, changesInVersion6 } from './converters';

export class RollerShutterConfiguration implements Configuration {
    $version = 6;
    type: 'roller_shutter' | 'external_blind' = 'roller_shutter';
    name = '';
    price: number | null = null;
    listPrice: number | null = null;
    priceNoMargin: number | null = null;
    listPriceNoMargin: number | null = null;
    priceAfterDiscountsInCurrency: number | null = null;
    listPriceAfterDiscountsInCurrency: number | null = null;
    priceBeforePromotions: number | null = null;
    listPriceBeforePromotions: number | null = null;
    priceGross: number | null = null;
    listPriceGross: number | null = null;
    priceGrossAfterDiscounts: number | null = null;
    listPriceGrossAfterDiscounts: number | null = null;
    priceGrossBeforePromotions: number | null = null;
    listPriceGrossBeforePromotions: number | null = null;
    quantity = 1;
    priceSegments: PriceSegment[] = [];
    listPriceSegments: PriceSegment[] = [];
    priceSegmentsNoMargin: PriceSegment[] = [];
    listPriceSegmentsNoMargin: PriceSegment[] = [];
    priceSegmentsBeforePromotions: PriceSegment[] = [];
    listPriceSegmentsBeforePromotions: PriceSegment[] = [];
    priceParts: PricePart[] = [];
    listPriceParts: PricePart[] = [];
    pricePartsNoMargin: PricePart[] = [];
    listPricePartsNoMargin: PricePart[] = [];
    pricePartsBeforePromotions: PricePart[] = [];
    listPricePartsBeforePromotions: PricePart[] = [];
    discountGroups: DiscountGroups = {};
    discountGroupsNoMargin: DiscountGroups = {};
    title = '';
    description = '';
    attachments: any[] = [];
    deletedAttachments: any[] = [];
    valid = {};
    accessories: Accessory[] = [];
    rollerShutter: RollerShutter = null;
    timeLimit: number | null = null;
    timeLimitsStack: TimeLimitSegment[] = [];
    parameters: RollerShutterParameters = {
        weight: null,
    };
    attributes: {
        id: string;
        name: string;
        value: string;
        valueId?: number;
    }[] = [];

    static converters = {
        4: changesInVersion4,
        6: changesInVersion6,
    };

    constructor(
        configuration?,
        dataRequiredToUpdate?: any,
        conversion = true,
        configuratorsDataService?
    ) {
        if (RollerShutterConfiguration.is(configuration) && conversion) {
            this.runConversion(configuration, dataRequiredToUpdate);
        } else {
            if (RollerShutterConfiguration.is(configuration)) {
                for (const propName in configuration) {
                    if (this.hasOwnProperty(propName)) {
                        this[propName] = configuration[propName];
                    }
                }
            } else {
                if (RollerShutterActiveConfiguration.is(configuration)) {
                    this.name = configuration.Name;
                    this.price = configuration.Price;
                    this.listPrice = configuration.ListPrice;
                    this.priceNoMargin = configuration.PriceNoMargin;
                    this.listPriceNoMargin = configuration.ListPriceNoMargin;
                    this.priceAfterDiscountsInCurrency = configuration.PriceAfterDiscountsInCurrency || configuration.Price;
                    this.listPriceAfterDiscountsInCurrency = configuration.ListPriceAfterDiscountsInCurrency || configuration.ListPrice;
                    this.priceGross = configuration.PriceGross || configuration.Price;
                    this.listPriceGross = configuration.PriceGross || configuration.ListPrice;
                    this.priceGrossAfterDiscounts = configuration.PriceGrossAfterDiscounts || configuration.Price;
                    this.listPriceGrossAfterDiscounts = configuration.ListPriceGrossAfterDiscounts || configuration.ListPrice;
                    this.priceBeforePromotions = configuration.PriceBeforePromotions;
                this.listPriceBeforePromotions = configuration.ListPriceBeforePromotions;
                    this.quantity = configuration.Quantity;
                    this.priceSegments = configuration.PriceSegments;
                    this.listPriceSegments = configuration.ListPriceSegments;
                    this.priceSegmentsNoMargin = configuration.PriceSegmentsNoMargin;
                    this.listPriceSegmentsNoMargin = configuration.ListPriceSegmentsNoMargin;
                    this.priceSegmentsBeforePromotions = configuration.PriceSegmentsBeforePromotions;
                    this.listPriceSegmentsBeforePromotions = configuration.ListPriceSegmentsBeforePromotions;
                    this.priceParts = configuration.PriceParts;
                    this.listPriceParts = configuration.ListPriceParts;
                    this.pricePartsNoMargin = configuration.PricePartsNoMargin;
                    this.listPricePartsNoMargin = configuration.ListPricePartsNoMargin;
                    this.pricePartsBeforePromotions = configuration.PricePartsBeforePromotions;
                    this.listPricePartsBeforePromotions = configuration.ListPricePartsBeforePromotions;
                    this.discountGroups = configuration.DiscountGroups;
                    this.discountGroupsNoMargin = configuration.DiscountGroupsNoMargin;
                    this.title = configuration.Title;
                    this.description = configuration.Description;
                    this.attachments = configuration.Attachments;
                    this.valid = configuration.valid;
                    this.timeLimit = configuration.timeLimit;
                    this.timeLimitsStack = configuration.timeLimitsStack;
                    this.attributes = configuration.attributes;
                    if (configuration.Accessories) {
                        this.accessories = configuration.Accessories.map<Accessory>(
                            accessory => new Accessory(accessory)
                        );
                    }
                    configuration.RollerShutter.weight = configuration.Weight;
                    this.rollerShutter = new RollerShutter(configuration.RollerShutter);
                    this.parameters = {
                        weight: configuration.Weight,
                    };
                }
            }
        }
    }

    public static is(configuration): configuration is RollerShutterConfiguration {
        return configuration instanceof RollerShutterConfiguration || configuration.$version;
    }

    protected runConversion(configuration, dataRequiredToUpdate: any) {
        let updatedConfiguration = core.copy(configuration);

        for (let version = configuration.$version + 1; version <= this.$version; version++) {
            updatedConfiguration.$version = version;
            if (RollerShutterConfiguration.converters[version] && typeof RollerShutterConfiguration.converters[version] === 'function') {
                updatedConfiguration = RollerShutterConfiguration.converters[version](
                    updatedConfiguration,
                    dataRequiredToUpdate
                );
            }
        }
        (Object as any)
            .entries(updatedConfiguration)
            .forEach(([key, value]) => (this[key] = value));
    }
}
